<template>
  <module-template
    id="scrolltainer"
    title
    :canDelete="$store.state.bpInstDeletePermission && !locked"
    :canDeleteOldRuns="$store.state.bpInstDeletePermission && !locked"
    :canEdit="$store.state.bpInstRenamePermission"
    :canLock="$store.state.bpInstLockPermission && !locked"
    :canUnlock="$store.state.bpInstLockPermission && locked"
    :delete-confirm="false"
    canList
    @deleteItem="showDelete = true"
    @deleteOldRuns="deleteOldRuns"
    @editItem="editBp"
    @list="listBps"
    @lock="toggleLock"
    @unlock="toggleLock"
  >
    <template #title>
      <v-row dense class="px-1" align="center">
        {{ $t("global.business-process.bp-instance") }}
        <v-icon color="white" class="px-3" size="9">pic-arrow-right</v-icon>

        <div class="mr-2">
          {{ "#" + bpInstance?.id }}
        </div>

        <v-text-field
          dark
          v-model="editingName"
          type="text"
          v-if="showEdit"
          :rules="[noTagsRule]"
          style="width: 400px"
        >
          <template #append-outer>
            <v-btn
              @click="onEditConfirm"
              class="mr-1"
              dark
              fab
              outlined
              small
              :disabled="!canEditName"
            >
              <v-icon>pic-check</v-icon>
            </v-btn>

            <v-btn @click="showEdit = false" dark fab outlined small>
              <v-icon>pic-close</v-icon>
            </v-btn>
          </template>
        </v-text-field>
        <div v-else>{{ bpInstance?.title }}</div>
      </v-row>
    </template>

    <v-row dense class="mb-3">
      <v-col>
        <basic-card-horizontal
          :headers="leftCardHeaders"
          :item="leftCardItems"
          v-if="bpInstance"
        >
          <template #[`item.env`]="{ item }">
            <router-link :to="envRoute"> {{ item.env }} </router-link>
          </template>

          <template #[`item.definition`]="{ item }">
            <router-link :to="definitionRoute">
              {{ item.definition }}
            </router-link>
          </template>

          <template #[`item.createdBy`]="{ item }">
            <span v-if="item.isCreatedByDeleted">
              {{ $t("global.concepts.deleted-user") }}
            </span>

            <router-link
              v-else
              :to="createdByRoute"
              class="ooliba-font-color-blue"
            >
              {{ item.createdBy }}
            </router-link>
          </template>

          <template #[`item.localBp`]="{ item }">
            <v-checkbox
              v-model="item.localBp"
              dense
              disabled
              hide-details
              class="pt-0 mt-0"
            ></v-checkbox>
          </template>
        </basic-card-horizontal>
      </v-col>

      <v-col>
        <basic-card-horizontal
          :headers="rightCardHeaders"
          :item="rightCardItems"
          class="mb-2"
          v-if="bpInstance"
        >
        </basic-card-horizontal>

        <basic-card-horizontal
          :headers="statusCardHeaders"
          :item="statusCardItems"
          v-if="bpInstance"
        >
          <template #[`item.status`]="{ item }">
            {{ item.status }}
            <v-tooltip v-if="locked" top>
              <template #activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small>pic-lock-fill</v-icon>
              </template>
              <span>{{ lockMessage }}</span>
            </v-tooltip>
          </template>
        </basic-card-horizontal>
      </v-col>
    </v-row>

    <v-card>
      <BPInstanceImage
        v-if="flowUserImage.id"
        :flowInstanceId="bpInstance.flowInstanceId"
        :flowId="flow?.id"
        :flowUserImageWidth="flowUserImage?.width"
        :flowUserImageHeight="flowUserImage?.height"
        :flowUserImageZoomable="flowUserImage?.zoomable"
        :pause-updates="deleting"
        @click="onTaskClick"
      />
    </v-card>

    <task-details
      v-if="clickedTask && clickedTaskConfig"
      :flowTaskId="clickedTask.id"
      :id="clickedTaskConfig.id"
      :name="clickedTaskConfig.currentVersionName"
      :version="clickedTaskConfig.currentVersionNumber"
      :fileEnv="bpInstance?.fileEnvName"
      :locked="bpInstance?.lockUser"
      :is-created-by-deleted="bpInstance?.isCreatedByDeleted"
      :is-active="bpInstance?.isActive"
    />

    <v-dialog v-model="showDelete" persistent :width="500">
      <v-overlay absolute :value="deleting">
        <v-chip color="blue" dark>{{ $t("global.msg.deleting") }}</v-chip>
      </v-overlay>

      <v-card>
        <v-card-title>
          <v-icon color="warning" class="mr-2">pic-warning</v-icon>
          <span class="headline">{{ $t("global.action.confirm") }}</span>
        </v-card-title>

        <v-card-subtitle>
          {{ $t("global.business-process.delete-bp") }}
        </v-card-subtitle>

        <v-card-text>
          <div class="mt-4 font-weight-bold">
            {{ $t("global.action.confirm-continue-sure") }}
          </div>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            depressed
            data-cy-id="confirm-delete-button"
            color="error"
            class="is-action"
            @click="deleteBp()"
          >
            {{ $t("global.action.yes-delete") }}
          </v-btn>
          <v-btn depressed class="button" @click="showDelete = false">
            {{ $t("global.action.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteOldRuns" persistent :width="700">
      <v-overlay absolute :value="deletingOldRuns">
        <v-chip color="blue" dark>{{ $t("global.msg.deleting") }}</v-chip>
      </v-overlay>

      <v-overlay absolute :value="loadingOldRuns">
        <v-chip color="blue" dark>
          {{ $t("global.business-process.finding-old-runs") }}
        </v-chip>
      </v-overlay>

      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("global.action.confirm") }}</span>
        </v-card-title>

        <v-card-subtitle>
          {{ $t("global.business-process.delete-old-runs") }}
        </v-card-subtitle>

        <v-card-text>
          <basic-card-horizontal
            style="border: 1px solid #ccc"
            :headers="deleteOldRunsHeaders"
            :item="deleteOldRunsItems"
            colsLeft="6"
            colsRight="6"
          >
            <template #[`item.oldRuns`]="{ item }">
              <router-link
                v-if="item.oldRuns"
                :to="{
                  name: 'Old run deletion',
                  params: { bpId: bpInstance?.id },
                }"
                class="ooliba-font-color-blue"
              >
                {{ item.oldRuns }} ({{ $t("global.action.click-for-details") }})
              </router-link>
              <span v-else>{{ item.oldRuns }}</span></template
            >
          </basic-card-horizontal>

          <div v-if="totalRuns && oldRuns" class="mt-4 font-weight-bold">
            {{ $t("global.business-process.confirm-delete-old-runs") }}
          </div>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            v-if="!loadingOldRuns && totalRuns && oldRuns"
            depressed
            data-cy-id="confirm-delete-button"
            color="error"
            class="is-action"
            @click="confirmDeleteOldRuns"
          >
            {{ $t("global.action.yes-delete") }}
          </v-btn>

          <v-btn depressed class="button" @click="showDeleteOldRuns = false">
            <span v-if="!loadingOldRuns && totalRuns && oldRuns">
              {{ $t("global.action.cancel") }}</span
            >
            <span v-else> {{ $t("global.action.close") }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </module-template>
</template>

<script>
import { get, put, remove } from "@/model/api";
import TaskDetails from "@/components/businessProcess/TaskDetails";
import BPInstanceImage from "../../components/businessProcess/BPInstanceImage";
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import BasicCardHorizontal from "@/components/BasicCardHorizontal";
import { noTags } from "@/model/rules";
import { formatDate } from "@/model/util";

export default {
  name: "BPInstance",

  components: {
    ModuleTemplate,
    TaskDetails,
    BPInstanceImage,
    BasicCardHorizontal,
  },
  data() {
    return {
      leftCardHeaders: [
        {
          text: this.$t("global.business-process.definition"),
          value: "definition",
          class: "ooliba-font-color-blue",
        },
        { text: this.$t("global.concepts.created-on"), value: "dateCreated" },
        {
          text: this.$t("global.administration.owner"),
          value: "createdBy",
        },
        {
          text: this.$t("global.environment.environment"),
          value: "env",
          class: "ooliba-font-color-blue",
        },
        { text: this.$t("global.business-process.local-bp"), value: "localBp" },
      ],

      statusCardHeaders: [
        { text: this.$t("global.concepts.status"), value: "status" },
      ],

      bpInstance: {},
      flow: {},
      flowUserImage: {},
      clickedTask: undefined,
      clickedTaskConfig: undefined,

      showEdit: false,
      editingName: undefined,

      noTagsRule: noTags(this),

      deleteOldRunsHeaders: [
        {
          text: this.$t("global.business-process.total-runs"),
          value: "totalRuns",
        },
        {
          text: this.$t("global.business-process.cut-off-date"),
          value: "cutOffDate",
        },
        {
          text: this.$t("global.business-process.deleted-runs"),
          value: "oldRuns",
        },
      ],

      showDelete: false,
      deleting: false,

      showDeleteOldRuns: false,
      deletingOldRuns: false,
      totalRuns: undefined,
      oldRuns: undefined,
      loadingOldRuns: false,
      runDeletionMaxDate: undefined,

      timerId: undefined,
    };
  },

  computed: {
    leftCardItems() {
      const envName = this.bpInstance?.fileEnvIsLocal
        ? this.bpInstance?.parentFileEnvName
        : this.bpInstance?.fileEnvName;
      const envColor = this.bpInstance?.fileEnvIsLocal
        ? this.bpInstance?.parentFileEnvColor
        : this.bpInstance?.fileEnvColor;
      return {
        definition:
          this.bpInstance?.definitionName +
          " (v." +
          (this.bpInstance?.definitionVersion + 1) +
          ")",
        dateCreated: formatDate(this.bpInstance?.dateCreated),
        createdBy: this.bpInstance?.createdBy,
        isCreatedByDeleted: this.bpInstance?.isCreatedByDeleted,
        env: envName,
        envColor: envColor,
        localBp: this.bpInstance?.local,
      };
    },

    statusCardItems() {
      return {
        status: this.bpInstance?.isActive
          ? this.bpInstance?.status
          : this.$t("global.concepts.disabled").toUpperCase(),
      };
    },

    rightCardHeaders() {
      if (this.bpInstance?.props) {
        return Object.keys(this.bpInstance.props).map((prop) => {
          return { text: prop, value: prop };
        });
      }
      return [];
    },

    rightCardItems() {
      const item = {};
      this.rightCardHeaders.forEach((header) => {
        item[header.value] = this.bpInstance.props[header.value];
      });
      return item;
    },

    deleteOldRunsItems() {
      return {
        cutOffDate: this.runDeletionMaxDate,
        totalRuns: this.totalRuns,
        oldRuns: this.oldRuns,
      };
    },

    locked() {
      return !!this.bpInstance?.lockUser;
    },

    lockMessage() {
      const parsedDate = formatDate(this.bpInstance?.lockDate);
      let userName = this.bpInstance?.lockUser;
      if (this.bpInstance?.isLockUserDeleted) {
        userName = this.$t("global.concepts.deleted-user");
      }
      return this.$t("global.business-process.lock-message", [
        parsedDate,
        userName,
      ]);
    },

    envRoute() {
      if (!this.bpInstance?.parentFileEnvId && !this.bpInstance?.fileEnvId) {
        return {};
      }
      return {
        name: "File env",
        params: {
          fileEnvId: this.bpInstance.fileEnvIsLocal
            ? this.bpInstance.parentFileEnvId
            : this.bpInstance.fileEnvId,
        },
      };
    },

    definitionRoute() {
      if (!this.bpInstance?.definitionId) {
        return {};
      }
      return {
        name: "BP definition",
        params: {
          bpId: this.bpInstance.definitionId,
        },
      };
    },

    createdByRoute() {
      if (!this.bpInstance?.createdById) {
        return {};
      }
      return {
        name: "User",
        params: {
          userId: this.bpInstance.createdById,
        },
      };
    },

    canEditName() {
      return typeof this.noTagsRule(this.editingName) !== "string";
    },
  },

  methods: {
    formatDate,
    listBps() {
      this.$router.push({ name: "BP instances" });
    },

    continuouslyUpdateInstance() {
      this.timerId = window.setTimeout(async () => {
        if (this.bpInstance?.id) {
          if (!this.deleting) {
            this.bpInstance = await this.getBPInstance(this.bpInstance?.id);
          }
          this.continuouslyUpdateInstance();
        }
      }, 1800);
    },

    async getBPInstance(id) {
      return await get("/businessProcessInstance/" + id).catch((error) =>
        this.onError(error)
      );
    },

    async onTaskClick(vertexId) {
      this.clickedTask = await this.getTask(
        this.bpInstance.flowInstanceId,
        vertexId
      );
      this.clickedTaskConfig = await this.getTaskConfig(this.clickedTask?.name);
    },

    onError(error) {
      this.$store.commit("showError", error);
    },

    async getTask(flowInstanceId, vertexId) {
      return await get(
        "/flow-instance/flow-task/" + flowInstanceId + "/" + vertexId
      ).catch((error) => this.onError(error));
    },

    async getTaskConfig(taskConfigName) {
      return await get("/flow-task-config/" + taskConfigName).catch((error) =>
        this.onError(error)
      );
    },

    async deleteOldRuns() {
      if (!this.bpInstance) return;
      this.loadingOldRuns = true;
      this.showDeleteOldRuns = true;
      const response = await get(
        "/businessProcessInstance/check-old-run-deletion?id=" +
          this.bpInstance.id
      ).catch((error) => this.onError(error));
      this.totalRuns = response.totalRuns;
      this.oldRuns = response.oldRuns;
      this.loadingOldRuns = false;
    },

    async confirmDeleteOldRuns() {
      this.clickedTask = undefined;
      this.deletingOldRuns = true;
      await remove(
        "/businessProcessInstance/delete-old-runs?id=" + this.bpInstance.id
      ).catch((error) => this.onError(error));
      this.deletingOldRuns = false;
      this.showDeleteOldRuns = false;
    },

    async deleteBp() {
      this.deleting = true;
      await remove("/businessProcessInstance?id=" + this.bpInstance.id)
        .then(() => {
          this.$router.push({ name: "BP instances" });
          this.$store.commit("removeFromRecentBps", this.bpInstance.id);
        })
        .catch((error) => {
          this.onError(error);
        });
      this.showDelete = false;
      this.deleting = false;
    },

    onEditConfirm() {
      put(
        "/businessProcessInstance/rename?instanceId=" +
          encodeURIComponent(this.bpInstance.id) +
          "&name=" +
          encodeURIComponent(this.editingName)
      )
        .then((r) => {
          this.editingName = r;
          this.bpInstance.title = r;
          this.showEdit = false;
        })
        .catch((e) => {
          this.$store.commit("showError", e);
          this.showEdit = false;
        });
    },

    toggleLock() {
      put(
        "/businessProcessInstance/toggle-lock/" +
          this.bpInstance?.id +
          "?lock=" +
          !this.bpInstance?.lockUser
      )
        .then((r) => {
          this.bpInstance = r;
        })
        .catch((error) => {
          this.$store.commit("showError", error);
        });
    },

    editBp() {
      this.showEdit = true;
    },

    async getFlow(flowInstancId) {
      return await get("/flow-instance/flow/" + flowInstancId).catch(
        (error) => {
          this.$store.commit("showError", error);
        }
      );
    },
  },

  async created() {
    const bpId = this.$route.params.bpId;
    this.bpInstance = await this.getBPInstance(bpId);

    this.runDeletionMaxDate = await get(
      "/environment-property/run-deletion-max-date"
    ).catch((error) => {
      this.$store.commit("showError", error);
    });

    if (this.bpInstance) {
      this.$store.commit("addToRecentBps", {
        id: bpId,
        name: this.bpInstance?.title,
      });
      this.flow = await this.getFlow(this.bpInstance?.flowInstanceId);
      this.flowUserImage = this.flow?.userImage;
      this.editingName = this.bpInstance?.title;
    }
    this.continuouslyUpdateInstance();
  },

  destroyed() {
    clearTimeout(this.timerId);
  },
};
</script>
